
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import AttendanceTable from '../components/AttendanceTable';
import {
  Box,
  TextField,
  Button,
  MenuItem,
  InputLabel,
  Select,
  FormControl,
  Typography,
  Card,
  CardContent,
  Alert,
  Chip,
} from '@mui/material';
import { FileDownload, Search } from '@mui/icons-material';
import { masterApi } from '../../../api/masterApi';
import { attendanceApi } from '../../../api/attendanceApi';
import { DropdownItem, AttendanceRecord } from '../../../types';
import { toast } from 'react-toastify';

const ALL_RECORDS_PAGE_SIZE = 100000;

const AttendanceRecordsPage: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const tenantId = user?.tenantId;
  const today = new Date().toISOString().split('T')[0];

  // Filter state
  const [department, setDepartment] = useState('');
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [dateRange, setDateRange] = useState({ from: today, to: today });
  const [dateErrors, setDateErrors] = useState({ from: '', to: '' });
  const [searchText, setSearchText] = useState('');
  const [searchField, setSearchField] = useState<'entityName' | 'recordedBy' | 'remarks'>('entityName');
  const [tableSearchText, setTableSearchText] = useState('');

  // Applied filters used for fetch/export
  const [appliedFilters, setAppliedFilters] = useState({
    department: '',
    type: '',
    status: '',
    from: today,
    to: today,
    searchText: '',
    searchField: 'entityName' as 'entityName' | 'recordedBy' | 'remarks',
  });

  // Dropdown data
  const [departments, setDepartments] = useState<DropdownItem[]>([]);

  // Table data
  const [records, setRecords] = useState<AttendanceRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [dateSortDirection, setDateSortDirection] = useState<'asc' | 'desc' | undefined>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Load dropdown data
  useEffect(() => {
    masterApi.getDepartments().then(setDepartments).catch(() => {});
  }, []);

  // Fetch attendance records
  const fetchRecords = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const searchFilter = appliedFilters.searchText.trim();
      const hasTableSearch = tableSearchText.trim().length > 0;

      const result = await attendanceApi.getAttendanceRecords({
        departmentId: appliedFilters.department || undefined,
        type: appliedFilters.type || undefined,
        status: appliedFilters.status || undefined,
        startDate: appliedFilters.from || undefined,
        endDate: appliedFilters.to || undefined,
        ...(searchFilter ? { [appliedFilters.searchField]: searchFilter } : {}),
        sortDirection: dateSortDirection,
        pageNumber: hasTableSearch ? 1 : page + 1,
        pageSize: hasTableSearch ? ALL_RECORDS_PAGE_SIZE : (pageSize === -1 ? ALL_RECORDS_PAGE_SIZE : pageSize),
      });
      setRecords(result.data);
      setTotal(hasTableSearch ? result.data.length : result.total);
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to fetch attendance records';
      setError(msg);
      setRecords([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, dateSortDirection, page, pageSize, tableSearchText]);

  // Load records on mount and when page/pageSize change
  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  useEffect(() => {
    if (records.length === 0 && tableSearchText) {
      setTableSearchText('');
    }
  }, [records, tableSearchText]);

  useEffect(() => {
    if (tableSearchText.trim()) {
      setPage(0);
    }
  }, [tableSearchText]);

  const validateDateRange = () => {
    const errors = {
      from: dateRange.from ? '' : 'From date is required',
      to: dateRange.to ? '' : 'To date is required',
    };

    if (dateRange.from && dateRange.to && dateRange.to < dateRange.from) {
      errors.to = 'To date cannot be earlier than From date';
    }

    setDateErrors(errors);
    return !errors.from && !errors.to;
  };

  const handleSearch = () => {
    if (!validateDateRange()) {
      toast.error('From date and To date are mandatory');
      return;
    }

    setAppliedFilters({
      department,
      type,
      status,
      from: dateRange.from,
      to: dateRange.to,
      searchText,
      searchField,
    });
    setPage(0);
  };

  const handleExport = async () => {
    if (!validateDateRange()) {
      toast.error('From date and To date are mandatory');
      return;
    }

    try {
      const searchFilter = appliedFilters.searchText.trim();
      const blob = await attendanceApi.exportRecords({
        departmentId: appliedFilters.department || undefined,
        type: appliedFilters.type || undefined,
        status: appliedFilters.status || undefined,
        startDate: appliedFilters.from || undefined,
        endDate: appliedFilters.to || undefined,
        ...(searchFilter ? { [appliedFilters.searchField]: searchFilter } : {}),
        tableSearch: tableSearchText.trim() || undefined,
        sortDirection: dateSortDirection,
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `attendance_records_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Attendance records exported successfully');
    } catch (err: any) {
      // When responseType is 'blob', error body is also a Blob — parse it to read the message
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const json = JSON.parse(text);
          toast.error(json.message || 'Failed to export records');
          return;
        } catch {
          // fall through to generic message
        }
      }
      toast.error(err.response?.data?.message || 'Failed to export records');
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(0);
  };

  const searchFieldLabelMap: Record<'entityName' | 'recordedBy' | 'remarks', string> = {
    entityName: 'Entity Name',
    recordedBy: 'Recorded By',
    remarks: 'Remarks',
  };

  const selectedDepartmentName = departments.find((d) => String(d.id) === appliedFilters.department)?.name;

  const appliedFilterChips = [
    appliedFilters.department && selectedDepartmentName
      ? { label: 'Department', value: selectedDepartmentName }
      : null,
    appliedFilters.type ? { label: 'Type', value: appliedFilters.type } : null,
    appliedFilters.status ? { label: 'Status', value: appliedFilters.status } : null,
    appliedFilters.from ? { label: 'From', value: appliedFilters.from } : null,
    appliedFilters.to ? { label: 'To', value: appliedFilters.to } : null,
    appliedFilters.searchText.trim()
      ? {
          label: `Search (${searchFieldLabelMap[appliedFilters.searchField]})`,
          value: appliedFilters.searchText.trim(),
        }
      : null,
  ].filter((item): item is { label: string; value: string } => Boolean(item));

  const tableFilteredRecords = useMemo(() => {
    const query = tableSearchText.trim().toLowerCase();
    if (!query) return records;

    return records.filter((row) =>
      [
        row.employeeName,
        row.employeeCode,
        row.department,
        row.date,
        row.time,
        row.type,
        row.modeOfEntry,
        row.locationName,
        row.recordedBy,
        row.attendanceStatus,
        row.checkInTime,
        row.checkOutTime,
        row.isLate ? 'Late' : '',
        row.isEarlyCheckOut ? 'Early Checkout' : '',
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [records, tableSearchText]);

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        View Attendance Records
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        View and manage employee attendance logs.        
      </Typography>

      {/* Filters Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
            Filters
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Department</InputLabel>
              <Select
                value={department}
                label="Department"
                onChange={(e) => setDepartment(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {departments.map((d) => (
                  <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          <TextField
              label="From"
              type="date"
              size="small"
              required
              InputLabelProps={{ shrink: true }}
              value={dateRange.from}
              onChange={(e) => {
                const value = e.target.value;

                setDateRange((prev) => ({
                  from: value,
                  to: prev.to && value && prev.to < value ? '' : prev.to,
                }));
                setDateErrors((prev) => ({
                  ...prev,
                  from: value ? '' : 'From date is required',
                  to: dateRange.to && value && dateRange.to < value ? 'To date is required' : prev.to,
                }));
              }}
              inputProps={{
                max: today, // 🚫 disable future dates
              }}
              error={Boolean(dateErrors.from)}
              helperText={dateErrors.from}
            />

            <TextField
              label="To"
              type="date"
              size="small"
              required
              InputLabelProps={{ shrink: true }}
              value={dateRange.to}
              onChange={(e) => {
                const value = e.target.value;

                // ❌ prevent selecting date less than "from"
                if (!dateRange.from || value >= dateRange.from) {
                  setDateRange({ ...dateRange, to: value });
                  setDateErrors((prev) => ({
                    ...prev,
                    to: value ? '' : 'To date is required',
                  }));
                }
              }}
              inputProps={{
                min: dateRange.from || undefined,
                max: today, // 🚫 disable future dates
              }}
              error={
                Boolean(dateErrors.to) ||
                (dateRange.to !== '' &&
                  dateRange.from !== '' &&
                  dateRange.to < dateRange.from)
              }
              helperText={
                dateErrors.to ||
                (dateRange.to !== '' &&
                dateRange.from !== '' &&
                dateRange.to < dateRange.from
                  ? "To date cannot be earlier than From date"
                  : "")
              }
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={type}
                label="Type"
                onChange={(e) => setType(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="CHECK IN">Check In</MenuItem>
                <MenuItem value="CHECK OUT">Check Out</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={status}
                label="Status"
                onChange={(e) => setStatus(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="Present">Present</MenuItem>
                <MenuItem value="Absent">Absent</MenuItem>
                <MenuItem value="HalfDay">Half Day</MenuItem>
                <MenuItem value="Leave">Leave</MenuItem>
                <MenuItem value="Holiday">Holiday</MenuItem>
                <MenuItem value="CompOff">Comp Off</MenuItem>
              </Select>
            </FormControl>
            {/* <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Search In</InputLabel>
              <Select
                value={searchField}
                label="Search In"
                onChange={(e) => setSearchField(e.target.value as 'entityName' | 'recordedBy' | 'remarks')}
              >
                <MenuItem value="entityName">Entity Name</MenuItem>
                <MenuItem value="recordedBy">Recorded By</MenuItem>
                <MenuItem value="remarks">Remarks</MenuItem>
              </Select>
            </FormControl> */}
            {/* <TextField
              label="Search Text"
              size="small"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            /> */}
            <Button
              variant="contained"
              color="primary"
              startIcon={<Search />}
              onClick={handleSearch}
              sx={{ textTransform: 'none' }}
            >
              Search
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<FileDownload />}
              onClick={handleExport}
              sx={{ textTransform: 'none' }}
            >
              Export
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* {appliedFilterChips.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          {appliedFilterChips.map((item) => (
            <Chip key={`${item.label}-${item.value}`} label={`${item.label}: ${item.value}`} size="small" />
          ))}
        </Box>
      )} */}

      {records.length > 0 && (
        <Box sx={{ mb: 2, maxWidth: 320 }}>
          <TextField
            fullWidth
            size="small"
            label="Search In Table"
            placeholder="Type to filter list"
            value={tableSearchText}
            onChange={(e) => setTableSearchText(e.target.value)}
          />
        </Box>
      )}

      {/* Attendance Table */}
      <AttendanceTable
        records={tableFilteredRecords}
        loading={loading}
        total={tableSearchText.trim() ? tableFilteredRecords.length : total}
        page={tableSearchText.trim() ? 0 : page}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onDateSortChange={(direction) => {
          setDateSortDirection(direction);
          setPage(0);
        }}
      />
    </Box>
  );
};

export default AttendanceRecordsPage;
